import { RollbarProvider } from '@app/providers/RollbarProvider';
import React from 'react';
import type { NextPage } from 'next';
import type { AppContext, AppProps as NextAppProps } from 'next/app';
import NextApp from 'next/app';
import { useRouter } from 'next/router';
import { CookiesProvider } from '@evrone-erp/react-std';
import { CircularProgress } from '@mui/material';
import { NotificationsProvider } from '@shared/notifications/model/NotificationsProvider';
import { AppNotifications } from '@shared/notifications/ui/AppNotifications/AppNotifications';
import { AxiosErrorInterceptorProvider } from '@app/providers/AxiosErrorInterceptorProvider';
import type { TRuntimeConfig } from '@shared/lib/runtime-config/RuntimeConfigContext';
import { RuntimeConfigProvider } from '@shared/lib/runtime-config/RuntimeConfigContext';
import { getOtpEnabledFromEnv } from '@shared/lib/runtime-config/getOtpEnabledFromEnv';
import { QueryProvider } from './providers/QueryProvider';
import { ThemeProvider } from './providers/ThemeProvider';
type TAppProps = NextAppProps & {
  serverCookie?: string;
  runtimeConfig: TRuntimeConfig;
};
type TNextPageWithLayout = NextPage & {
  getLayout?: (page: JSX.Element) => React.ReactNode;
};
type TAppPropsWithLayout = TAppProps & {
  Component: TNextPageWithLayout;
};
export function App({
  Component,
  pageProps,
  serverCookie,
  runtimeConfig
}: TAppPropsWithLayout) {
  const {
    isReady
  } = useRouter();
  const getLayout = Component.getLayout ?? (page => page);
  if (!isReady) {
    return <CircularProgress />;
  }
  return <RuntimeConfigProvider config={runtimeConfig}>
      <QueryProvider>
        <NotificationsProvider>
          <RollbarProvider>
            <AxiosErrorInterceptorProvider>
              <CookiesProvider serverCookies={serverCookie}>
                <ThemeProvider>
                  {getLayout(<Component {...pageProps} />)}
                  <AppNotifications />
                </ThemeProvider>
              </CookiesProvider>
            </AxiosErrorInterceptorProvider>
          </RollbarProvider>
        </NotificationsProvider>
      </QueryProvider>
    </RuntimeConfigProvider>;
}
App.getInitialProps = async (context: AppContext) => {
  const appProps = await NextApp.getInitialProps(context);
  return {
    ...appProps,
    serverCookie: context.ctx.req?.headers?.cookie,
    runtimeConfig: {
      otpEnabled: getOtpEnabledFromEnv()
    }
  };
};